import { DesignDocument, DesignRun } from '../../../services/designRunsService';
import { PipelineCardStatus } from '../shared/PipelineCard';

export const SIMON_STEPS: Array<{ id: string; name: string }> = [
  { id: 'simon_outline', name: 'Outline course structure' },
  { id: 'simon_drafting', name: 'Draft slide copy' },
  { id: 'simon_level_check', name: 'CEFR level check' },
  { id: 'simon_validate', name: 'Validate against Simon limits' },
  { id: 'simon_assets', name: 'Generate audio & images' },
  { id: 'simon_videos', name: 'Render scenario videos' },
  { id: 'simon_compile', name: 'Build Package' },
];

export interface SimonStageState {
  status: PipelineCardStatus;
  statusText?: string;
  cost?: number;
  runCount?: number;
}

/** Per-card state for the Simon pipeline, derived from run.stages + document.
    simon_videos is virtual (no stage record): derived from the asset report. */
export function simonStageStates(run: DesignRun, doc: DesignDocument | null):
  Record<string, SimonStageState> {
  const realization = run.realizations?.simon;
  const active = realization?.status === 'realizing';
  const failed = realization?.status === 'failed';
  const byId: Record<string, { status?: string; total_cost_usd?: number; runs?: unknown[] }> = {};
  for (const s of run.stages ?? []) byId[s.stage_id] = s;

  const simonDoc = doc?.realizations?.simon;
  const blocks = simonDoc?.course_doc?.blocks ?? [];
  const sectionCount = blocks.filter((b) => b.type === 'section').length;
  const draftingRuns = byId.simon_drafting?.runs?.length ?? 0;
  const assets = simonDoc?.assets;
  const rendering = assets?.rendering_videos?.length ?? 0;
  const failedVideos = assets?.failed_videos?.length ?? 0;
  const videoTotal = (assets?.files ?? []).filter((f) => f.filename.endsWith('.mp4')).length;

  const out: Record<string, SimonStageState> = {};
  let runningSeen = false;
  for (const step of SIMON_STEPS) {
    if (step.id === 'simon_videos') {
      out[step.id] = videoTotal === 0
        ? { status: byId.simon_assets ? 'succeeded' : 'pending', statusText: byId.simon_assets ? 'No videos in this course' : undefined }
        : failedVideos ? { status: 'failed', statusText: `${failedVideos} of ${videoTotal} failed` }
        : rendering ? { status: 'running', statusText: `Rendering… ${videoTotal - rendering}/${videoTotal} done (HeyGen)` }
        : { status: 'succeeded', statusText: `Completed — ${videoTotal} video(s)` };
      continue;
    }
    const record = byId[step.id];
    const draftingDone = Boolean(byId.simon_validate) || (sectionCount > 0 && draftingRuns >= sectionCount);
    let status: PipelineCardStatus = 'pending';
    if (record?.status === 'succeeded' && !(step.id === 'simon_drafting' && active && !draftingDone)) {
      status = 'succeeded';
    } else if ((active || failed) && !runningSeen) {
      status = failed ? 'failed' : 'running';
      runningSeen = true;
    }
    let statusText: string | undefined;
    if (step.id === 'simon_drafting' && sectionCount) {
      statusText = status === 'succeeded' ? `Completed — ${sectionCount} sections`
        : status === 'running' ? `Drafting… ${Math.min(draftingRuns, sectionCount)}/${sectionCount} sections` : undefined;
    }
    if (step.id === 'simon_assets' && assets && status === 'succeeded') {
      statusText = `Completed — ${(assets.files ?? []).length} files`;
    }
    out[step.id] = {
      status,
      statusText,
      cost: record?.total_cost_usd ? Number(record.total_cost_usd) : undefined,
      runCount: record?.runs?.length ?? 0,
    };
  }
  return out;
}
